(function () {
  const instances = new Map();

  class FloatingPromoBanner {
    constructor(section) {
      this.section = section;
      this.wrapper = section.querySelector('[data-promo-wrapper]');
      this.toggleButton = section.querySelector('[data-banner-toggle]');
      this.collapsedText = section.querySelector('[data-collapsed-text]');
      this.detailWrapper = section.querySelector('[data-banner-detail]');
      this.detailScroll = section.querySelector('[data-detail-scroll]');
      this.detailLists = Array.from(section.querySelectorAll('[data-detail-list]'));
      this.detailItems = Array.from(section.querySelectorAll('[data-detail-item]'));

      this.interval = parseInt(section.dataset.rotationInterval, 10) || 3000;
      this.currentIndex = 0; // group index
      this.timer = null;
      this.isOpen = false;

      // Portal 相关状态：避免祖先 transform 影响 position: fixed
      this._originalParent = null;
      this._placeholder = null;
      this._isPortaled = false;

      // 折叠栏跑马灯相关状态
      this.tickerContainer = null;
      this.tickerTrack = null;
      this.tickerX = 0;
      this.tickerSpeed = 0.6; // px per frame，控制连续滚动速度
      this._tickerRaf = null;

      if (!this.wrapper || !this.toggleButton || !this.detailLists.length) {
        return;
      }


      this.init();
    }

    init() {
      this.groupsCount = this.detailLists.length;
      this.renderCollapsed(0);
      this.highlightGroup(0);

      // 运行时 portal 到 body，规避 transform 祖先导致的 fixed 失效
      this.attachToHeaderContainer();
      this.syncWithHeaderVisibility();
      
      // 初始化时更新 header 顶部偏移，确保导航不被覆盖
      this.updateHeaderOffset();
      // 初始化测量一次下拉高度，保证首次动画距离正确
      this.updateDetailHeight();
      // 等高度变量写入后再显示下拉面板，避免刷新时闪现“默认展开”
      window.requestAnimationFrame(() => {
        if (this.section) {
          this.section.classList.add('is-ready');
        }
      });

      if (this.groupsCount > 1) {
        this.startRotation();
      }

      this.toggleButton.addEventListener('click', (e) => {
        this.handleToggle(e);
        // 展开/收起后文本高度可能变化，重算偏移
        this.updateHeaderOffset();
      });
      document.addEventListener('click', this.handleDocumentClick);
      this.section.addEventListener('mouseenter', this.pauseRotation);
      this.section.addEventListener('mouseleave', this.handleMouseLeave);

      // 窗口尺寸变化时重算偏移
      window.addEventListener('resize', this.updateHeaderOffset, { passive: true });
      window.addEventListener('orientationchange', this.updateHeaderOffset);
      window.addEventListener('resize', this.updateDetailHeight, { passive: true });
      window.addEventListener('orientationchange', this.updateDetailHeight);

      // 整项点击跳转
      this.detailItems.forEach((item) => {
        item.addEventListener('click', (e) => {
          const link = item.dataset.link;
          if (!link) {
            return;
          }
          // 避免内部按钮或文本选择冲突
          if (e.target.closest('.floating-promo-banner__list-button')) {
            window.location.href = link;
            return;
          }
          window.location.href = link;
        });
      });
    }

    updateHeaderOffset = () => {
      try {
        // 使用折叠按钮的高度作为顶部偏移，避免覆盖导航
        const h = this.wrapper ? this.wrapper.getBoundingClientRect().height : 0;
        const offset = Math.ceil(h);
        document.documentElement.style.setProperty('--promo-banner-height', `${offset}px`);
      } catch (e) {
        // 忽略错误，保持默认 0
      }
    };

    updateDetailHeight = () => {
      try {
        if (!this.section || !this.detailWrapper || !this.detailScroll) {
          return;
        }

        // 与 CSS 保持一致：detail scroll 最大高度为 60vh
        const maxHeightPx = Math.floor(window.innerHeight * 0.6);

        // scrollHeight 通常包含 padding，可作为“面板内容高度”基准
        const contentHeight = Math.ceil(this.detailScroll.scrollHeight || 0);
        const h = Math.max(0, Math.min(contentHeight, maxHeightPx));
        this.section.style.setProperty('--promo-detail-height', `${h}px`);
      } catch (e) {
        // 安静失败：保持默认变量即可
      }
    };

    handleToggle = (event) => {
      event.stopPropagation();
      // 仅当当前组允许下拉时展开
      const currentGroup = this.detailLists[this.currentIndex];
      const dropdownEnabled = currentGroup && currentGroup.dataset.dropdownEnabled === 'true';
      const itemCount = currentGroup ? currentGroup.querySelectorAll('[data-detail-item]').length : 0;
      const withinAllowed = itemCount >= 1 && itemCount <= 8;
      const groupLink = currentGroup ? (currentGroup.dataset.groupLink || '') : '';

      // 若不满足下拉条件，则尝试跳转到组链接
      if (!(dropdownEnabled && withinAllowed)) {
        if (groupLink) {
          window.location.href = groupLink;
        }
        return;
      }

      // 切换前刷新高度变量，保证滑入/滑出距离准确
      this.updateHeaderOffset();
      this.updateDetailHeight();

      this.isOpen = !this.isOpen;
      this.wrapper.classList.toggle('is-open', this.isOpen);
      this.toggleButton.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');

      if (this.isOpen) {
        this.pauseRotation();
        this.scrollDetailToActive();
      } else if (this.groupsCount > 1) {
        this.startRotation();
      }
    };

    handleDocumentClick = (event) => {
      if (!this.isOpen) {
        return;
      }

      if (this.section.contains(event.target)) {
        return;
      }

      this.closeDetail();
    };

    handleMouseLeave = () => {
      if (!this.isOpen) {
        this.startRotation();
      }
    };

    startRotation = () => {
      if (this.timer || this.groupsCount <= 1 || this.isOpen) {
        return;
      }

      this.timer = window.setInterval(() => {
        this.showNext();
      }, this.interval);
    };

    pauseRotation = () => {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    };

    showNext() {
      const nextIndex = (this.currentIndex + 1) % this.groupsCount;
      this.showIndex(nextIndex);
    }

    showIndex(index) {
      if (index === this.currentIndex) {
        return;
      }

      this.currentIndex = index;
      this.renderCollapsed(index);
      this.highlightGroup(index);
      // 保持纯文本展示，不使用跑马灯
      if (this.isOpen) {
        this.updateDetailHeight();
        this.scrollDetailToActive();
      }
    }

    renderCollapsed(index) {
      const group = this.detailLists[index];
      if (!group) {
        return;
      }
      const title = group.dataset.groupTitle || '';
      // 若未启用跑马灯容器，则保持纯文本回退
      if (!this.tickerContainer) {
        this.collapsedText.textContent = title;
      }
      const dropdownEnabled = group.dataset.dropdownEnabled === 'true';
      const itemCount = group.querySelectorAll('[data-detail-item]').length;
      const withinAllowed = itemCount >= 1 && itemCount <= 8;
      this.toggleButton.classList.toggle('has-dropdown', dropdownEnabled && withinAllowed);
    }

    highlightGroup(index) {
      this.detailLists.forEach((groupEl, groupIndex) => {
        groupEl.classList.toggle('is-active', groupIndex === index);
      });
    }

    scrollDetailToActive() {
      if (!this.detailScroll) {
        return;
      }
      // 展开时滚动到顶部，便于完整展示
      this.detailScroll.scrollTop = 0;
    }

    closeDetail() {
      if (!this.isOpen) {
        return;
      }

      this.isOpen = false;
      this.wrapper.classList.remove('is-open');
      this.toggleButton.setAttribute('aria-expanded', 'false');

      if (this.groupsCount > 1) {
        this.startRotation();
      }
    }

    destroy() {
      this.pauseRotation();
      this.stopTicker();
      document.removeEventListener('click', this.handleDocumentClick);
      this.section.removeEventListener('mouseenter', this.pauseRotation);
      this.section.removeEventListener('mouseleave', this.handleMouseLeave);
      window.removeEventListener('resize', this.updateDetailHeight);
      window.removeEventListener('orientationchange', this.updateDetailHeight);
      if (this.toggleButton) {
        this.toggleButton.removeEventListener('click', this.handleToggle);
      }

      if (this._headerObserver) {
        this._headerObserver.disconnect();
        this._headerObserver = null;
      }

      // 销毁时回挂到原位置，兼容主题编辑器 section:unload
      this.restoreFromPortal();
    }

    // -----------------
    // 跑马灯：连续循环滚动实现
    // -----------------
    setupTicker() {
      if (!this.collapsedText) return;
      // 创建跑马灯容器与轨道
      this.collapsedText.textContent = '';
      const container = document.createElement('div');
      container.className = 'floating-promo-banner__ticker';
      const track = document.createElement('div');
      track.className = 'floating-promo-banner__ticker-track';
      container.appendChild(track);
      this.collapsedText.appendChild(container);
      this.collapsedText.classList.add('has-ticker');
      this.tickerContainer = container;
      this.tickerTrack = track;
      // 初始内容来自第一个组
      this.buildTickerForGroup(this.currentIndex);
      this.startTicker();
    }

    collectGroupTitles(index) {
      const group = this.detailLists[index];
      if (!group) return [];
      const items = Array.from(group.querySelectorAll('[data-detail-item]'));
      const titles = items
        .map((el) => el.querySelector('.floating-promo-banner__list-title')?.textContent?.trim())
        .filter((t) => Boolean(t));
      return titles;
    }

    buildTickerForGroup(index) {
      if (!this.tickerTrack || !this.tickerContainer) return;
      this.stopTicker();
      this.tickerTrack.innerHTML = '';
      let titles = this.collectGroupTitles(index);
      if (!titles.length) {
        const fallback = this.detailLists[index]?.dataset.groupTitle || '';
        titles = fallback ? [fallback] : [];
      }

      titles.forEach((t) => {
        const item = document.createElement('span');
        item.className = 'floating-promo-banner__ticker-item';
        item.textContent = t;
        this.tickerTrack.appendChild(item);
      });

      // 复制直到长度足够，避免末尾出现空白
      const containerWidth = this.tickerContainer.clientWidth || 0;
      let trackWidth = this.tickerTrack.scrollWidth;
      let safety = 0;
      while (trackWidth < containerWidth * 2 && safety < 10) {
        Array.from(this.tickerTrack.children).forEach((child) => {
          this.tickerTrack.appendChild(child.cloneNode(true));
        });
        trackWidth = this.tickerTrack.scrollWidth;
        safety++;
      }
      this.tickerX = 0;
      this.applyTickerTransform();
    }

    applyTickerTransform() {
      if (!this.tickerTrack) return;
      this.tickerTrack.style.transform = `translateX(${this.tickerX}px)`;
    }

    startTicker() {
      if (this._tickerRaf || this.isOpen || !this.tickerTrack) return;
      this._tickerRaf = window.requestAnimationFrame(this.stepTicker);
    }

    stopTicker() {
      if (this._tickerRaf) {
        window.cancelAnimationFrame(this._tickerRaf);
        this._tickerRaf = null;
      }
    }

    stepTicker = () => {
      if (!this.tickerTrack) return;
      this.tickerX -= this.tickerSpeed;

      const first = this.tickerTrack.firstElementChild;
      if (first) {
        const firstWidth = first.getBoundingClientRect().width;
        const styles = window.getComputedStyle(this.tickerTrack);
        const gap = parseFloat(styles.gap || '0') || 0;
        if (-this.tickerX >= firstWidth + gap) {
          this.tickerX += firstWidth + gap;
          this.tickerTrack.appendChild(first);
        }
      }

      this.applyTickerTransform();
      this._tickerRaf = window.requestAnimationFrame(this.stepTicker);
    };

    // -----------------
    // Portal：在运行时将 section 移到 body 顶层
    // -----------------
    hasTransformAncestor(el) {
      let node = el && el.parentElement;
      while (node && node !== document.body) {
        const cs = window.getComputedStyle(node);
        const hasTransform = (cs.transform && cs.transform !== 'none') ||
          (cs.perspective && cs.perspective !== 'none') ||
          (cs.willChange && cs.willChange.includes('transform'));
        if (hasTransform) return true;
        node = node.parentElement;
      }
      return false;
    }

    isIOSSafari() {
      const ua = navigator.userAgent;
      const isIOS = /iP(ad|hone|od)/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
      return isIOS && isSafari;
    }

    attachToHeaderContainer() {
      if (!this.section) return;
      // 不再搬移 DOM，只做样式对齐与回退处理
      this.section.style.position = 'relative';
      this.section.style.top = '';
      this.section.style.left = '';
      this.section.style.right = '';
      this.section.style.zIndex = '';

      // 如果在 Header 组（.shopify-section-group-header-group）中，则交由样式控制 sticky
      const inHeaderGroup = this.section.closest('.shopify-section-group-header-group');
      if (!inHeaderGroup) {
        // 非 Header 组：保持原位显示，sticky 行为由 JS 同步类实现
      }
    }

    // 监听 Header 隐藏/显示状态，并同步到 Banner，使其一起隐藏
    syncWithHeaderVisibility() {
      try {
        this.headerSection = document.getElementById('shopify-section-header');
        if (!this.headerSection || !this.section) return;

        const apply = () => {
          const hidden = this.headerSection.classList.contains('shopify-section-header-hidden');
          const sticky = this.headerSection.classList.contains('shopify-section-header-sticky');
          this.section.classList.toggle('is-hidden', hidden);
          this.section.classList.toggle('shopify-section-header-sticky', sticky);
        };

        // 初始同步一次
        apply();

        // 观察 header 的 class 变化
        this._headerObserver = new MutationObserver(apply);
        this._headerObserver.observe(this.headerSection, { attributes: true, attributeFilter: ['class'] });
      } catch (e) {
        console.error('[FloatingPromoBanner] syncWithHeaderVisibility error', e);
      }
    }

    /* duplicate updateHeaderOffset removed to avoid conflicts */

    restoreFromPortal() {
      if (!this._isPortaled || !this._originalParent) return;
      try {
        if (this._placeholder && this._placeholder.parentNode === this._originalParent) {
          this._originalParent.insertBefore(this.section, this._placeholder);
          this._originalParent.removeChild(this._placeholder);
        } else {
          this._originalParent.appendChild(this.section);
        }
      } catch (e) {
        // 安静失败：在编辑器卸载时节点可能已被移除
      }
      this._isPortaled = false;
      this.section.classList.remove('is-portaled');
      this._originalParent = null;
      this._placeholder = null;
    }
  }

  function init(section) {
    if (instances.has(section)) {
      return;
    }

    const instance = new FloatingPromoBanner(section);
    if (instance.wrapper) {
      instances.set(section, instance);
    }
  }

  function destroy(section) {
    const instance = instances.get(section);
    if (!instance) {
      return;
    }
    instance.destroy();
    instances.delete(section);
  }

  function initAll(context = document) {
    const sections = context.querySelectorAll('.floating-promo-banner');
    sections.forEach(init);
  }

  document.addEventListener('DOMContentLoaded', () => initAll());

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const sections = event.target.querySelectorAll('.floating-promo-banner');
    sections.forEach(destroy);
  });
})();
